import React, { useId, useState } from 'react';
import { Typography } from './Typography';

// Form Field Component
export const FormField = ({ 
  label, 
  children, 
  error, 
  required = false, 
  helpText,
  helperText, // Support both prop names
  className = '',
  ...props 
}) => {
  // Use helperText if provided, otherwise fall back to helpText
  const displayText = helperText || helpText;
  
  return (
    <div className={`form-group ${className}`} {...props}>
      {label && (
        <label className="form-label">
          {label}
          {required && <span className="text-danger-dark ml-1">*</span>}
        </label>
      )}
      {children}
      {displayText && (
        <p className="text-sm text-text-muted mt-1">{displayText}</p>
      )}
      {error && (
        <p className="text-sm text-danger-dark mt-1 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

// Enhanced Input Component
export const Input = ({ 
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  disabled = false,
  className = '',
  helperText, // Extract helperText to prevent it from being passed to DOM
  helpText,   // Extract helpText to prevent it from being passed to DOM
  ...props 
}) => {
  const baseClasses = 'form-input focus-ring transition-smooth bg-field';
  const errorClasses = error ? 'error' : '';
  const disabledClasses = disabled ? 'cursor-not-allowed opacity-90' : '';

  // Handle NaN values and ensure value is never null
  // For date and time inputs, don't use isNaN check as it can incorrectly treat valid strings as NaN
  const safeValue = (value === null || value === undefined) ? '' : value;

  return (
    <input
      type={type}
      placeholder={placeholder}
      value={safeValue}
      onChange={onChange}
      disabled={disabled}
      className={`${baseClasses} ${errorClasses} ${disabledClasses} ${className}`}
      {...props}
    />
  );
};

// Enhanced Select Component
export const Select = ({ 
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  error,
  disabled = false,
  className = '',
  helperText, // Extract helperText to prevent it from being passed to DOM
  helpText,   // Extract helpText to prevent it from being passed to DOM
  ...props 
}) => {
  const baseClasses = 'form-input form-select--custom-arrow focus-ring transition-smooth appearance-none bg-field';
  const errorClasses = error ? 'error' : '';
  const disabledClasses = disabled ? 'cursor-not-allowed opacity-90' : '';

  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
        className={`${baseClasses} ${errorClasses} ${disabledClasses} ${className}`}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

// Enhanced Textarea Component
export const Textarea = ({ 
  placeholder,
  value,
  onChange,
  error,
  disabled = false,
  rows = 4,
  className = '',
  helperText, // Extract helperText to prevent it from being passed to DOM
  helpText,   // Extract helpText to prevent it from being passed to DOM
  ...props 
}) => {
  const baseClasses = 'form-input focus-ring transition-smooth resize-none bg-field';
  const errorClasses = error ? 'error' : '';
  const disabledClasses = disabled ? 'cursor-not-allowed opacity-90' : '';

  return (
    <textarea
      placeholder={placeholder}
      value={value || ''} // Ensure value is never null
      onChange={onChange}
      disabled={disabled}
      rows={rows}
      className={`${baseClasses} ${errorClasses} ${disabledClasses} ${className}`}
      {...props}
    />
  );
};

// Checkbox Component
export const Checkbox = ({  
  label,
  checked,
  onChange,
  error,
  disabled = false,
  className = '',
  helperText, // Extract helperText to prevent it from being passed to DOM
  helpText,   // Extract helpText to prevent it from being passed to DOM
  ...props 
}) => (
  <div className={`flex items-start space-x-3 ${className}`}>
    <div className="flex items-center h-5">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="w-4 h-4 rounded border-border-medium text-primary focus:ring-primary focus:ring-2 disabled:opacity-50 bg-field"
        {...props}
      />
    </div>
    <div className="flex-1">
      <label className="text-sm font-medium text-text-primary cursor-pointer">
        {label}
      </label>
      {error && (
        <p className="text-sm text-danger-dark mt-1">{error}</p>
      )}
    </div>
  </div>
);

// Toggle switch for on/off settings
export const Switch = ({
  checked = false,
  onChange,
  disabled = false,
  id,
  name,
  className = '',
  ...props
}) => (
  <button
    type="button"
    role="switch"
    id={id}
    aria-checked={checked}
    disabled={disabled}
    onClick={() => {
      if (disabled) return;
      onChange?.({
        target: { name, checked: !checked, type: 'checkbox' },
      });
    }}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? 'border-primary bg-primary' : 'border-border-medium bg-surface-subtle'
    } ${className}`}
    {...props}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-surface shadow-brand-sm transition-transform ${
        checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
      }`}
      aria-hidden
    />
  </button>
);

// Radio Group Component
export const RadioGroup = ({ 
  label,
  options = [],
  value,
  onChange,
  error,
  disabled = false,
  className = '',
  helperText, // Extract helperText to prevent it from being passed to DOM
  helpText,   // Extract helpText to prevent it from being passed to DOM
  ...props 
}) => (
  <div className={className} {...props}>
    {label && (
      <label className="form-label">{label}</label>
    )}
    <div className="space-y-2">
      {options.map((option) => (
        <div key={option.value} className="flex items-center">
          <input
            type="radio"
            id={option.value}
            name={option.name || 'radio-group'}
            value={option.value}
            checked={value === option.value}
            onChange={onChange}
            disabled={disabled}
            className="w-4 h-4 text-primary border-border-medium focus:ring-primary focus:ring-2 disabled:opacity-50 bg-surface"
          />
          <label htmlFor={option.value} className="ml-2 text-sm font-medium text-text-primary cursor-pointer">
            {option.label}
          </label>
        </div>
      ))}
    </div>
    {error && (
      <p className="text-sm text-danger-dark mt-1">{error}</p>
    )}
  </div>
);

// Form Section Component
export const FormSection = ({
  title,
  description,
  children,
  className = '',
  collapsible = false,
  defaultOpen = true,
  ...props
}) => {
  const sectionId = useId();
  const [open, setOpen] = useState(defaultOpen);
  const canCollapse = Boolean(collapsible && title);

  if (!canCollapse) {
    return (
      <div className={`rounded-lg border border-border bg-surface-subtle p-4 sm:p-6 ${className}`} {...props}>
        {title && (
          <div className="mb-6">
            <Typography variant="h4" className="mb-2 text-text-primary">{title}</Typography>
            {description && (
              <Typography variant="body-small" className="text-text-secondary">{description}</Typography>
            )}
          </div>
        )}
        <div className="space-y-6">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-border bg-surface-subtle ${className}`} {...props}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left sm:p-6"
        aria-expanded={open}
        aria-controls={`${sectionId}-panel`}
        id={`${sectionId}-toggle`}
      >
        <span className="min-w-0">
          <Typography variant="h4" className="text-text-primary">{title}</Typography>
          {description ? (
            <Typography variant="body-small" className="mt-1 text-text-secondary">
              {description}
            </Typography>
          ) : null}
        </span>
        <svg
          className={`h-5 w-5 shrink-0 text-text-secondary transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open ? (
        <div
          id={`${sectionId}-panel`}
          role="region"
          aria-labelledby={`${sectionId}-toggle`}
          className="space-y-6 border-t border-border px-4 py-4 sm:px-6 sm:py-6"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

// Form Grid Component
export const FormGrid = ({ 
  columns = 2,
  children,
  className = '',
  ...props 
}) => {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <div className={`grid ${gridClasses[columns]} gap-6 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Form Actions Component
export const FormActions = ({ 
  children,
  className = '',
  ...props 
}) => (
  <div className={`flex flex-col sm:flex-row gap-3 pt-6 border-t border-border ${className}`} {...props}>
    {children}
  </div>
);

// Date Input Component
export const DateInput = ({ 
  label,
  value,
  onChange,
  error,
  disabled = false,
  min,
  max,
  className = '',
  ...props 
}) => (
  <FormField label={label} error={error}>
    <div className="relative">
      <Input
        type="date"
        value={value}
        onChange={onChange}
        disabled={disabled}
        min={min}
        max={max}
        className={className}
        {...props}
      />
    </div>
  </FormField>
);

// Time Input Component
export const TimeInput = ({ 
  label,
  value,
  onChange,
  error,
  disabled = false,
  className = '',
  step = 60, // Default to 60 seconds (1 minute) to hide seconds
  ...props 
}) => (
  <FormField label={label} error={error} className="min-w-0">
    <div className="relative min-w-0 w-full">
      <Input
        type="time"
        value={value}
        onChange={(e) => {
          // Handle both direct value and event object
          if (typeof onChange === 'function') {
            onChange(e.target.value);
          }
        }}
        disabled={disabled}
        className={`min-w-0 max-w-full ${className}`}
        step={step}
        {...props}
      />
    </div>
  </FormField>
);

// Number Input Component
export const NumberInput = ({ 
  label,
  value,
  onChange,
  error,
  disabled = false,
  min,
  max,
  step,
  allowEmpty = false,
  className = '',
  ...props 
}) => {
  const handleChange = (e) => {
    if (!allowEmpty) {
      onChange(e);
      return;
    }
    const raw = e.target.value;
    if (raw === '') {
      onChange({ ...e, target: { ...e.target, name: e.target.name, value: '' } });
      return;
    }
    const digitsOnly = raw.replace(/\D/g, '');
    onChange({ ...e, target: { ...e.target, name: e.target.name, value: digitsOnly } });
  };

  return (
    <FormField label={label} error={error}>
      <Input
        type={allowEmpty ? 'text' : 'number'}
        inputMode={allowEmpty ? 'numeric' : undefined}
        value={value ?? ''}
        onChange={handleChange}
        disabled={disabled}
        min={allowEmpty ? undefined : min}
        max={allowEmpty ? undefined : max}
        step={allowEmpty ? undefined : step}
        className={className}
        {...props}
      />
    </FormField>
  );
};

// Currency Input Component
export const CurrencyInput = ({ 
  label,
  value,
  onChange,
  error,
  disabled = false,
  currency = 'GBP',
  className = '',
  onCurrencyChange, // Remove this from DOM props
  ...props 
}) => {
  // Filter out non-DOM props
  const { currencyCode, ...domProps } = props;
  
  return (
    <FormField label={label} error={error}>
      <div className="flex items-center border border-border-medium rounded-md focus-within:border-primary focus-within:ring-2 focus-within:ring-[var(--color-focus)]">
        <div className="px-3 py-2 bg-surface-subtle border-r border-border-medium rounded-l-md">
          <span className="text-text-muted text-sm font-medium select-none">
            {currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency}
          </span>
        </div>
        <input
          type="number"
          value={value || ''} // Ensure value is never null
          onChange={onChange}
          disabled={disabled}
          step="0.01"
          min="0"
          className={`flex-1 min-w-0 px-3 py-2 border-0 rounded-r-md bg-field text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-0 ${disabled ? 'cursor-not-allowed opacity-90' : ''} ${className}`}
          {...domProps} // Only pass DOM-safe props
        />
      </div>
    </FormField>
  );
};

export default {
  FormField,
  Input,
  Select,
  Textarea,
  Checkbox,
  Switch,
  RadioGroup,
  FormSection,
  FormGrid,
  FormActions,
  DateInput,
  TimeInput,
  NumberInput,
  CurrencyInput
};
